import { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import { useSelector, useDispatch } from "react-redux";
import _merge from "lodash/merge";
import Cookies from "js-cookie";

import SectionDescription from "components/lib/Shared/SectionDescription";
import contactPersonValidation from "./contactPersonValidation";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from "components/lib/Shared/Icons/sflIcons";
import {
  getContactPersonCandidatesAsync,
  updateCompanyAsync,
  updateCompanyState,
} from "state/slices/company";
import Input from "components/lib/Shared/Input";
import ReactSelectWithFormik from "components/lib/Shared/DropdownSelect";
import Button from "components/lib/Shared/Button";
import { updateCompanyOfficialAsync } from "state/slices/tables/companyOfficialsSlice";
import { Constants } from "config/constants";
import { editRole, getRoles } from "state/slices/role";
import { CORPORATES } from "config/sflData";
import { editPermission, getPermissions } from "state/slices/permission";
import { Roles_Permissions_Constants } from "config/roles-permissions";
import { getFullName } from "utils";
import { updateTransaction } from "state/slices/transaction";

const ContactPerson = ({ handleBackBtn, handleNextBtn, transactions }) => {
  const { company, contactPersonCandidates, getContactPersonCandidatesStatus } =
    useSelector((state) => state.companySlice);
  const { transactionUnderReview } = useSelector(
    (state) => state.transactionSlice
  );
  const { officials, } = transactionUnderReview.items;
  const contactPersonTransaction = transactions.find(
    (trx) =>
      trx.applicationType === Constants.APPLICANT_CAPACITIES.COMPANY_SECRETARY
  );
  const newCandidates = officials
    .filter(
      (official) =>
        official.designation ===
        Constants.APPLICANT_CAPACITIES.COMPANY_SECRETARY
    )
    .map((official) => ({
      fullName: getFullName(official?.names),
      emailAddress: official?.electronicAddress?.emailAddress,
      mobileNumber: official?.electronicAddress?.mobileNumber,
      designation: official?.designation,
      idType: official?.idType,
      _id: official?._id,
    }));
  const currentRegistrar = company.contactPerson;
  const allContactPersonCandidates = [currentRegistrar, ...newCandidates];
  const [registrarAlreadyExist, setRegistrarAlreadyExist] = useState(false);
  const [selectedContactPerson, setSelectedContactPerson] = useState({
    ...company?.contactPerson,
    label: company?.contactPerson?.fullName ?? "",
    value: company?.contactPerson?._id ?? "",
  });
  const [initialFormValues, setInitialFormValues] = useState({
    fullName: company?.contactPerson?.fullName ?? "",
    emailAddress: company?.contactPerson?.emailAddress ?? "",
    designation: company?.contactPerson?.designation ?? "",
    mobileNumber: company?.contactPerson?.mobileNumber ?? "",
  });
  const { permissions } = useSelector((state) => state.permissionSlice);
  const { roles } = useSelector((state) => state.roleSlice);

  const dispatch = useDispatch();

  useEffect(async () => {
    dispatch(getContactPersonCandidatesAsync({ companyId: company._id }));
    dispatch(
      getRoles({
        [CORPORATES.includes(selectedContactPerson.idType)
          ? "corporate"
          : "individual"]: selectedContactPerson.value,
        company: company._id,
      })
    );
    dispatch(
      getPermissions({
        [CORPORATES.includes(selectedContactPerson.idType)
          ? "corporate"
          : "individual"]: selectedContactPerson.value,
        company: company._id,
      })
    );
  }, []);

  const handleSubmit = async (formData) => {
    try {
      const companyData = {
        contactPerson: formData,
      };
      let rolesToUpdate = {};
      if (roles?.length > 0) {
        rolesToUpdate = {
          ...roles[0],
          dashboardRoles: [
            ...roles[0]?.dashboardRoles,
            {
              name: Roles_Permissions_Constants.DASHBOARD_ROLES.REGISTRAR,
              isDefault: true,
            },
          ],
        };
      } else {
        rolesToUpdate = {
          dashboardRoles: [
            {
              name: Roles_Permissions_Constants.DASHBOARD_ROLES.REGISTRAR,
              isDefault: true,
            },
          ],
        };
      }

      let permissionToUpdate = {};
      if (permissions?.length > 0) {
        permissionToUpdate = {
          ...permissions[0],
          dashboardPermissions:
            Roles_Permissions_Constants.DASHBOARD_PERMISSIONS_AGAINST_DESIGNATIONS[
              initialFormValues.designation
            ].REGISTRAR.map((permission) => ({
              name: permission,
              isDefault: true,
            })),
        };
      } else {
        permissionToUpdate = {
          dashboardPermissions:
            Roles_Permissions_Constants.DASHBOARD_PERMISSIONS_AGAINST_DESIGNATIONS[
              initialFormValues.designation
            ].REGISTRAR.map((permission) => ({
              name: permission,
              isDefault: true,
            })),
        };
      }

      if (currentRegistrar._id !== selectedContactPerson.value) {
        dispatch(
          updateTransaction({
            trxId: contactPersonTransaction._id,
            data: companyData,
          })
        );
        dispatch(editRole({ roleId: rolesToUpdate._id, data: rolesToUpdate }));
        dispatch(
          editPermission({
            permissionId: permissionToUpdate._id,
            data: permissionToUpdate,
          })
        );
        await dispatch(
          updateCompanyOfficialAsync({
            officialId: selectedContactPerson.value,
            data: {
              isRegistrar: true,
              companyId: company._id,
              idType: selectedContactPerson.idType,
            },
          })
        );
      } else {
      }
      handleNextBtn();
    } catch (err) { }
  };

  useEffect(() => {
    const populateRegistrar = () => {
      setRegistrarAlreadyExist(
        allContactPersonCandidates.find((official) => official.isRegistrar)
      );
      const companySecretary = allContactPersonCandidates.find(
        (official) => official.designation === "Company Secretary"
      );
      if (companySecretary) {
        setSelectedContactPerson({
          ...companySecretary,
          value: companySecretary._id,
          label: companySecretary.fullName,
        });
        return;
      }
    };
    populateRegistrar();
  }, []);

  useEffect(() => {
    if (selectedContactPerson.value) {
      setInitialFormValues(
        allContactPersonCandidates.find(
          (contactPerson) => contactPerson._id === selectedContactPerson.value
        )
      );
    }
  }, [selectedContactPerson]);

  const { schemaInitialValues, schema } = contactPersonValidation();
  const initialValues = _merge(schemaInitialValues, initialFormValues);

  const requiredFields = Object.keys(schema.fields).reduce((acc, curr) => {
    if (schema.fields[curr].tests.some((t) => t.OPTIONS.name === "required")) {
      acc.push(curr);
    }
    return acc;
  }, []);

  return (
    <Formik
      enableReinitialize
      initialValues={{
        ...initialValues,
        requiredFields,
      }}
      validationSchema={schema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, errors }) => {
        return (
          <Form className="mt-4">
            <SectionDescription
              title="Company Contact Person / Registrar Information"
              description="Its essential for a company to have a designated contact person, please select or add a designated contact person from the dropdown."
            />
            <div className="bg-white rounded-sm shadow-sm sm:rounded md:rounded-md">
              <div className="px-4 py-6 space-y-6 sm:p-6">
                <div className="grid grid-cols-6 gap-6">
                  <div className="col-span-6 sm:col-span-2">
                    <Field
                      name="selectedContactPerson"
                      id="selectedContactPerson"
                      placeholder="Select official"
                      component={ReactSelectWithFormik}
                      isDisabled={allContactPersonCandidates.length === 1}
                      showRequiredAsterisk
                      label="Official"
                      value={
                        selectedContactPerson.value ? selectedContactPerson : ""
                      }
                      onChange={setSelectedContactPerson}
                      options={allContactPersonCandidates.map(
                        (contactPerson) => ({
                          label: contactPerson.fullName,
                          value: contactPerson._id,
                        })
                      )}
                      isSearchable
                      className="w-full p-2 text-sm rounded-md bg-formInput text-primary placeholder-primary focus:outline-none focus:ring focus:ring-indigo-100 focus:border-indigo-300"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-6 gap-6">
                  <div className="col-span-6 sm:col-span-3">
                    <Field
                      name="fullName"
                      id="fullName"
                      placeholder="John Doe"
                      component={Input}
                      disabled
                      label="Full Name"
                      className="w-full p-2 text-sm rounded-md bg-formInput text-primary placeholder-primary focus:outline-none focus:ring focus:ring-indigo-100 focus:border-indigo-300"
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <Field
                      name="emailAddress"
                      id="emailAddress"
                      placeholder="john@gmail.com"
                      component={Input}
                      disabled
                      label="Email Address"
                      className="w-full p-2 text-sm rounded-md bg-formInput text-primary placeholder-primary focus:outline-none focus:ring focus:ring-indigo-100 focus:border-indigo-300"
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <Field
                      name="mobileNumber"
                      id="mobileNumber"
                      placeholder="Add registration number"
                      component={Input}
                      disabled
                      label="Telephone Number"
                      className="w-full p-2 text-sm rounded-md bg-formInput text-primary placeholder-primary focus:outline-none focus:ring focus:ring-indigo-100 focus:border-indigo-300"
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <Field
                      name="designation"
                      id="designation"
                      placeholder="Director"
                      component={Input}
                      disabled
                      label="Designation"
                      className="w-full p-2 text-sm rounded-md bg-formInput text-primary placeholder-primary focus:outline-none focus:ring focus:ring-indigo-100 focus:border-indigo-300"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-between py-3">
              <button
                type="button"
                onClick={handleBackBtn}
                className="flex items-start float-right py-2 font-semibold rounded text-primary mt-7"
              >
                <span className="flex items-center mr-4 h-7">
                  <ChevronLeftIcon
                    className="flex-shrink-0 w-5 h-5"
                    aria-hidden="true"
                  />
                </span>
                <p className="font-semibold">Go back</p>
              </button>

              <button
                disabled={isSubmitting}
                type="submit"
                className="flex items-start float-right py-2 font-semibold rounded text-primary mt-7"
              >
                <p className="font-semibold">
                  {isSubmitting ? "Submitting..." : "Next Step"}
                </p>
                <span className="flex items-center ml-4 h-7">
                  <ChevronRightIcon
                    className="flex-shrink-0 w-5 h-5"
                    aria-hidden="true"
                  />
                </span>
              </button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ContactPerson;
